import { APIError } from "@certn/api";
import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Space,
  showToast,
  useColors,
} from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { CategoryLayout } from "@certn/forms/src/layouts/CategoryLayout";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import {
  getUnmetAddressHistoryPeriodRequirement,
  type Residence,
} from "@certn/utils";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NotStarted } from "./NotStarted";
import { useApplication } from "../ApplicationContext";

const RESIDENCES_SCOPE_PATTERN = /\/properties\/residences$/;

function uischemaRendersResidences(uischema: unknown): boolean {
  if (!uischema || typeof uischema !== "object") {
    return false;
  }

  const node = uischema as { scope?: string; elements?: unknown[] };

  if (
    typeof node.scope === "string" &&
    RESIDENCES_SCOPE_PATTERN.test(node.scope)
  ) {
    return true;
  }

  return Array.isArray(node.elements)
    ? node.elements.some(uischemaRendersResidences)
    : false;
}

function SectionLayout(props: CascadiaLayoutProps) {
  const { t } = useTranslation();
  const colors = useColors();
  const { isSummary, isSubmitted, readonly, isSubmitting, values, schema } =
    useContext(CascadiaJsonFormContext);

  const {
    prevSection,
    navigateToSection,
    partiallySaveData,
    completedSections,
  } = useApplication();

  const sectionRendersResidences = useMemo(
    () => uischemaRendersResidences(props.uischema),
    [props.uischema]
  );

  const residencesSchema = (
    schema as {
      properties?: {
        residences?: {
          minMonths?: number;
          nzCurrentRegionMinMonths?: number;
        };
      };
    }
  )?.properties?.residences;

  const isPeriodRequirementUnmet = useMemo(() => {
    // Only gate the section that actually renders the residences control. The
    // schema in context is the whole application schema, so `residences` is
    // present on every section — without this guard we would disable "Next
    // step" on unrelated sections too (e.g. after pressing "Back").
    if (
      !sectionRendersResidences ||
      !residencesSchema?.nzCurrentRegionMinMonths
    ) {
      return false;
    }

    const residences = (values as { residences?: Residence[] } | undefined)
      ?.residences;

    return getUnmetAddressHistoryPeriodRequirement(residences, {
      minMonths: residencesSchema.minMonths,
      nzCurrentRegionMinMonths: residencesSchema.nzCurrentRegionMinMonths,
    });
  }, [sectionRendersResidences, residencesSchema, values]);

  if (isSummary) {
    const sectionKey = (props.uischema as any)?.name;
    const isCompleted = completedSections?.includes(sectionKey);

    return (
      <BaseLayout testID="section-layout" {...props} className={styles.summary}>
        {!isCompleted && !readonly && (
          <NotStarted
            sectionKey={sectionKey}
            showErrors={Boolean(isSubmitted)}
          />
        )}
      </BaseLayout>
    );
  }

  return (
    <>
      <CategoryLayout {...props} />
      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid ${colors["border-base"]}`,
        })}
      >
        <ButtonGroup justify="end">
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          <PrimaryButton
            type="submit"
            variant="brand-primary"
            isLoading={isSubmitting}
            isDisabled={isPeriodRequirementUnmet}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>
    </>
  );
}

const styles = {
  summary: css({
    display: "flex",
    flexDirection: "column",
    gap: 0,
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
};

SectionLayout.displayName = "SectionLayout";

export const SectionLayoutRenderer = withCascadiaLayoutProps(SectionLayout);
